import { alpha } from '@mui/material/styles';
import { ENABLED_ROW_HOVER_SELECTOR } from '@sharedCR/coreDesignSystem/Table/TableCDS/TableCDS.constants';

/** The person-avatar disc is a soft brand tint (colour, not an outline). */
const PERSON_AVATAR_TINT_OPACITY = 0.12;

export const rootStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
  minWidth: 0,
};

export const headerRowStyles = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '12px',
};

export const footerRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  flexWrap: 'wrap',
};

const stateBoxStyles = {
  padding: '16px',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: ({ palette }) => palette.divider,
  backgroundColor: ({ palette }) => palette.background.paper,
};

const loadingBoxStyles = {
  ...stateBoxStyles,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const columnCellStyles = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const actorCellStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
};

// A person gets a soft brand-tinted disc (color, not outline); a System/Service glyph keeps the
// neutral rounded tile so the two actor kinds still read apart.
export const getActorAvatarStyles = isPerson => ({
  flexShrink: 0,
  width: '28px',
  height: '28px',
  borderRadius: isPerson ? '50%' : '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: ({ palette }) =>
    isPerson ? alpha(palette.primary.main, PERSON_AVATAR_TINT_OPACITY) : palette.background.default,
  border: isPerson ? 'none' : '1px solid',
  borderColor: ({ palette }) => palette.divider,
  color: ({ palette }) => (isPerson ? palette.primary.main : palette.text.secondary),
});

export const actorTextStyles = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

export const fieldsCellStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap',
  minWidth: 0,
};

// A decorated row stacks its scope chips under the operation pill.
export const operationCellStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
};

// A decorated row's fields sit over a one-line note (the cell's second line).
export const decoratedFieldsCellStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
  whiteSpace: 'normal',
};

/** How far a muted (not effective) row or field fades back from full contrast. */
const MUTED_OPACITY = 0.56;

export const mutedFieldStyles = {
  opacity: MUTED_OPACITY,
};

// Rows the consumer decorates as muted fade back. Passing `getRowProps` turns on TableCDS's
// clickable-row hover (pointer + hover fill); these rows are not clickable — the expand toggle is —
// so that hover is neutralised for the whole table.
export const decoratedTableStyles = {
  '& .table-body .table-row[data-row-muted="true"] .table-cell': {
    opacity: MUTED_OPACITY,
  },
  [`& .table-body .table-row${ENABLED_ROW_HOVER_SELECTOR}`]: {
    cursor: 'default',
    backgroundColor: 'transparent',
  },
};

// The expanded drill-in is an inset gray card: set in from the row edges with its own fill + radius
// so its bounds (where the detail starts and ends inside the table) read at a glance.
export const entryDetailStyles = {
  margin: '0 16px 12px',
  padding: '16px',
  borderRadius: '8px',
  backgroundColor: ({ palette }) => palette.background.secondary,
};

export const detailTabsRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '12px',
};

export const snapshotToolbarStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const detailStateStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const getStateMessageStyles = ({ isFramed, isLoading }) => {
  if (!isFramed) {
    return detailStateStyles;
  }

  return isLoading ? loadingBoxStyles : stateBoxStyles;
};

export const detailHeaderStyles = {
  marginBottom: '8px',
};

// diff rows: field · was · →became
const diffRowStyles = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 1fr) minmax(140px, 1.4fr) minmax(140px, 1.4fr)',
  columnGap: '16px',
  alignItems: 'baseline',
  padding: '8px 12px',
};

// The diff reads as a filled panel on the detail surface — a tinted header strip + zebra rows carry
// the structure, so there are no per-row rules.
export const changesTableStyles = {
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: ({ palette }) => palette.background.paper,
};

export const diffHeaderStyles = {
  ...diffRowStyles,
  backgroundColor: ({ palette }) => palette.background.secondary,
};

export const getDiffBodyRowStyles = isOdd => ({
  ...diffRowStyles,
  backgroundColor: ({ palette }) => (isOdd ? palette.action.hover : 'transparent'),
});

export const monoValueStyles = {
  fontFamily: 'monospace',
  wordBreak: 'break-word',
};

export const oldValueStyles = {
  ...monoValueStyles,
  color: ({ palette }) => palette.text.secondary,
  textDecoration: 'line-through',
};

export const becameValueStyles = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  minWidth: 0,
};

// A summarised change has no Was/Became pair: its renderer spans both value columns.
export const summaryValueStyles = {
  gridColumn: '2 / -1',
  minWidth: 0,
};

export const changesPanelsStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

// A titled, outlined card on the detail surface: a tinted header strip, then rows edge to edge.
export const changesPanelStyles = {
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: ({ palette }) => palette.divider,
  backgroundColor: ({ palette }) => palette.background.paper,
};

export const changesPanelHeaderStyles = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  padding: '10px 14px',
  borderBottom: '1px solid',
  borderColor: ({ palette }) => palette.divider,
  backgroundColor: ({ palette }) => palette.background.secondary,
};

export const changesPanelBodyStyles = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  '& > :not(:last-child)': {
    borderBottom: '1px solid',
    borderColor: ({ palette }) => palette.divider,
  },
};

// The field panel lists one field per row: its name, then the old value struck before the new one.
export const inlineChangesStyles = {
  display: 'flex',
  flexDirection: 'column',
};

export const getInlineChangeRowStyles = isAutomatic => ({
  display: 'flex',
  gap: '12px',
  padding: '10px 14px',
  backgroundColor: ({ palette }) => (isAutomatic ? palette.background.secondary : 'transparent'),
  '&:not(:last-of-type)': {
    borderBottom: '1px solid',
    borderColor: ({ palette }) => palette.divider,
  },
});

export const fieldLabelStyles = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  wordBreak: 'break-word',
};

export const inlineFieldLabelStyles = {
  ...fieldLabelStyles,
  flexShrink: 0,
  width: '110px',
};

export const inlineValueStyles = {
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  columnGap: '8px',
  rowGap: '2px',
  flexGrow: 1,
  minWidth: 0,
};

export const diffArrowStyles = {
  flexShrink: 0,
  color: ({ palette }) => palette.text.secondary,
};

export const snapshotPanesStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '12px',
};

export const snapshotPaneStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
};

// The before/after block is framed by its paper fill against the detail's gray surface — the fill
// delineates it, no outline needed. The `regular12` variant supplies the size; monospace is the
// one accepted font override here (no CDS mono variant exists) for a raw JSON/diff block.
export const snapshotBlockStyles = {
  margin: 0,
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: ({ palette }) => palette.background.paper,
  fontFamily: 'monospace',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const SNAPSHOT_HIGHLIGHT_OPACITY = 0.16;

export const snapshotLineStyles = {
  display: 'block',
};

// A changed field's line is tinted in each pane: removed-red on Before, added-green on After, so the
// snapshot shows WHAT changed, not just the two full states.
export const getSnapshotLineStyles = isBefore => ({
  display: 'block',
  borderRadius: '2px',
  backgroundColor: ({ palette }) =>
    alpha(isBefore ? palette.error.main : palette.success.main, SNAPSHOT_HIGHLIGHT_OPACITY),
});
